import { RETURN_DISPOSAL, RETURN_DISPOSAL_REQUEST_MESSAGE, RTS_PARCEL_INVOICE_PATTERN } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypeRtsParcelInvoiceGroup, TypeRtsParcelInvoiceInput } from '~/types/receive.types';

/**
 * [입고관리 > 회송/폐기 관리] 회수(택배) 신청 송장번호 입력 검증 순수 함수.
 *
 * 에러 우선순위는 형식 오류 > 모달 내 중복 > 다른 이슈카드 중복 순이다.
 * 다른 이슈카드 중복은 택배사+송장번호 조합으로 서버가 신청 실패 응답에서 알려주므로,
 * 클라이언트 재검증은 값이 바뀌지 않은 행의 그 에러를 지우지 않고 유지한다.
 */

const { INVOICE_FORMAT_ERROR, INVOICE_DUP_IN_MODAL, INVOICE_DUP_IN_SYSTEM } = RETURN_DISPOSAL_REQUEST_MESSAGE;

/** 입력된 송장번호별 등장 횟수 (공백만 있는 값은 세지 않는다) */
const countInvoiceValues = (groups: TypeRtsParcelInvoiceGroup[]): Map<string, number> => {
  const valueCounts = new Map<string, number>();

  groups.forEach((group) =>
    group.invoices.forEach((invoice) => {
      const value = invoice.value.trim();
      if (value !== '') valueCounts.set(value, (valueCounts.get(value) ?? 0) + 1);
    })
  );

  return valueCounts;
};

/** 송장 입력 전체 재검증 — 형식 → 모달 내 중복 순으로 에러를 다시 계산하고, 둘 다 통과한 행의 서버 중복 에러는 유지한다. */
export const revalidateRtsParcelInvoices = (groups: TypeRtsParcelInvoiceGroup[]): TypeRtsParcelInvoiceGroup[] => {
  const valueCounts = countInvoiceValues(groups);

  const getInvoiceError = (invoice: TypeRtsParcelInvoiceInput): string | undefined => {
    const value = invoice.value.trim();
    if (value === '') return undefined;
    if (!RTS_PARCEL_INVOICE_PATTERN.test(value)) return INVOICE_FORMAT_ERROR;
    if ((valueCounts.get(value) ?? 0) > 1) return INVOICE_DUP_IN_MODAL;

    return invoice.error === INVOICE_DUP_IN_SYSTEM ? invoice.error : undefined;
  };

  return groups.map((group) => ({
    ...group,
    invoices: group.invoices.map((invoice) => ({ ...invoice, error: getInvoiceError(invoice) })),
  }));
};

/**
 * 입력 중(onChange) 해소된 모달 내 중복 에러 정리 — 한쪽 값을 고치면 짝이던 다른 행의 중복 에러도 즉시 사라진다.
 * 형식 에러는 포커스 이탈 시 판정하고 서버 중복 에러는 값 변경·택배사 변경으로만 풀리므로 여기서 건드리지 않는다.
 */
export const clearResolvedRtsParcelDuplicateErrors = (
  groups: TypeRtsParcelInvoiceGroup[]
): TypeRtsParcelInvoiceGroup[] => {
  const valueCounts = countInvoiceValues(groups);

  return groups.map((group) => ({
    ...group,
    invoices: group.invoices.map((invoice) => {
      if (invoice.error !== INVOICE_DUP_IN_MODAL) return invoice;

      const value = invoice.value.trim();
      return value === '' || (valueCounts.get(value) ?? 0) <= 1 ? { ...invoice, error: undefined } : invoice;
    }),
  }));
};

/**
 * 신청 실패 응답에서 다른 이슈카드에 이미 등록된 송장번호 목록을 꺼낸다.
 *
 * 같은 에러 코드를 형식 오류·개수 초과 같은 다른 송장 실패도 쓰기 때문에, `values` 전체가 문자열일 때만
 * 송장번호 목록으로 본다(개수 초과는 상한 숫자가 담겨 온다).
 */
export const getRegisteredRtsParcelInvoiceNos = (error?: TypeErrorResponseData['error']): string[] => {
  if (error?.code !== RETURN_DISPOSAL.INVOICE_NO_INVALID || !Array.isArray(error.values)) return [];

  const invoiceNos = error.values.filter((value): value is string => typeof value === 'string');
  if (invoiceNos.length !== error.values.length) return [];

  return invoiceNos.map((invoiceNo) => invoiceNo.trim()).filter((invoiceNo) => invoiceNo !== '');
};

/**
 * 서버가 알려준 송장번호와 값이 같은 행에 다른 이슈카드 중복 에러를 붙인다.
 *
 * 형식·모달 내 중복 에러가 이미 있는 행은 우선순위상 그 에러를 유지한다.
 * 매칭 수는 에러 유무와 무관하게 값이 일치한 행 수이며, 0 이면 호출부가 기존처럼 토스트로 안내한다.
 */
export const applyRegisteredRtsParcelInvoiceErrors = (
  groups: TypeRtsParcelInvoiceGroup[],
  invoiceNos: string[]
): { groups: TypeRtsParcelInvoiceGroup[]; matchedCount: number } => {
  const registeredInvoiceNos = new Set(invoiceNos);
  const isRegistered = (invoice: TypeRtsParcelInvoiceInput) => registeredInvoiceNos.has(invoice.value.trim());

  const matchedCount = groups.flatMap((group) => group.invoices).filter(isRegistered).length;
  const nextGroups = groups.map((group) => ({
    ...group,
    invoices: group.invoices.map((invoice) =>
      isRegistered(invoice) && !invoice.error ? { ...invoice, error: INVOICE_DUP_IN_SYSTEM } : invoice
    ),
  }));

  return { groups: nextGroups, matchedCount };
};

/** 택배사가 바뀌면 택배사+송장번호 조합이 달라져 서버 중복 판정의 근거가 사라지므로 그 에러만 해제한다. */
export const clearRegisteredRtsParcelInvoiceErrors = (
  groups: TypeRtsParcelInvoiceGroup[]
): TypeRtsParcelInvoiceGroup[] =>
  groups.map((group) => ({
    ...group,
    invoices: group.invoices.map((invoice) =>
      invoice.error === INVOICE_DUP_IN_SYSTEM ? { ...invoice, error: undefined } : invoice
    ),
  }));

/** 인라인 에러가 하나라도 남아 있는지 — 신청 버튼 비활성 조건 */
export const hasRtsParcelInvoiceError = (groups: TypeRtsParcelInvoiceGroup[]): boolean =>
  groups.some((group) => group.invoices.some((invoice) => !!invoice.error));
